import {Dispatch, SetStateAction} from "react";
import {toastType, ToastTypes} from "../../src/Toast/Toast";
import config from "./config";
import {LessonTopics} from "./api";
import { apiFetch, defaultHeaders } from "./headers";

type alertMessagesType = {
  success?: {
    text?: string
    textId?: string
  }
  error?: { text: string }
}

export type RadarReport = {
  theme: string
  metacog: number
}

export type RadarReportTheme = {
  theme: string
  alias: string
}

export type RadarReportResponse = {
  report: RadarReport[]
  themes: RadarReportTheme[]
}

export type TimeReport = {
  metacog: number
  entry_pattern: string
}

export type TimeReportResponse = {
  entries: {
    [key: string]: TimeReport
  }
  theme: string
}

export type ProgressReportResponse = {
  report: ProgressReport
}

export type ProgressReport = {
  driverId: string
  entries: ProgressEntry[]
}

export type ProgressEntry = {
  name: string
  progress: number
  themes: Map<string, ProgressTheme>
}

export type ProgressTheme = {
  theme: string
  progress: number
}

export type Module = {
  name: string
  progress: number
  competency: string
  lastActivity?: string
  moduleType: string
}

export type ModuleType = {
  name: string
  averageProgress: number
  averageCompetency: string
  modules: Module[]
}

export type ThemeProgress = {
  theme: string
  moduleTypes: ModuleType[]
}

export type ThemeRanking = {
  theme: string
  competency: string
  progress: number
}

export type TimeSeriesData = {
  progressOverTime: Array<{ date: string; averageProgress: number }>
  competencyOverTime: Array<{ date: string; averageCompetency: number }>
  lastActivityDate: string | null
  overallProgressAverage: number
  overallCompetencyAverage: number
}

export type ProgressPerModuleTypeResponse = {
  report: {
    themes: ThemeProgress[]
    strongestThemes: ThemeRanking[]
    weakestThemes: ThemeRanking[]
    timeSeriesData: TimeSeriesData
  }
}

const processResponse = async ({
                                 response,
                                 onAlert,
                                 messages,
                                 auth0,
                               }: {
  response: Response
  onAlert: Dispatch<SetStateAction<toastType | null>>
  messages?: alertMessagesType
  auth0?: boolean
}) => {
  if (
    response.status === 200 ||
    response.status === 201 ||
    response.status === 202
  ) {
    messages?.success &&
    onAlert({
      text: messages.success.text,
      type: messages.success ? ToastTypes.success : ToastTypes.error,
    })
    return auth0 ? response?.text() : response?.json?.()
  } else {
    const text = await response.text()
    try {
      const parsed = JSON.parse(text)
      onAlert({
        text: `${parsed.code}: ${parsed.message}`,
        type: ToastTypes.error,
      })
      console.warn(parsed.code, parsed.message)
    } catch (e) {
      onAlert({
        text: `${response.status}: ${response.statusText}`,
        type: ToastTypes.error,
      })
      console.warn(response.status, response.statusText)
    }
  }
}

const getElearningProgressData = ({
 userId,
 token,
 onAlert,
 licenseId,
}: {
  userId: string
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
  licenseId?: string
}): Promise<ProgressReportResponse> => {
  const url = `${config.apiUrl}/v1/elearning/instructor/driver/progress?driverId=${userId}${licenseId ? `&licenseId=${licenseId}` : ''}`;
    
  return apiFetch(url, {
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `Could not fetch Elearning data. ${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getElearningRadarReportData = ({
  userId,
  token,
  onAlert,
}: {
  userId: string
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<RadarReportResponse> => {
  const url = `${config.apiUrl}/v1/elearning/instructor/report/radar?driverId=${userId}`;
    
  return apiFetch(url, {
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `Could not fetch Elearning data. ${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getElearningTimeReportData = ({
  userId, theme, token, onAlert
}: {
  userId: string
  theme: string
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<TimeReportResponse> => {
  return apiFetch(`${config.apiUrl}/v1/elearning/instructor/report/time?theme=${theme}&driverId=${userId}`, {
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `Could not fetch Elearning data. ${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

const getElearningProgressPerModuleData = ({
  userId,
  licenseId,
  token,
  onAlert,
}: {
  userId: string
  licenseId: string
  token: string
  onAlert: Dispatch<SetStateAction<toastType | null>>
}): Promise<ProgressPerModuleTypeResponse> => {
  const url = `${config.apiUrl}/v1/elearning/instructor/progress/per-module?driverId=${userId}&licenseId=${licenseId}`;
    
  return apiFetch(url, {
    headers: defaultHeaders(token),
    method: 'GET',
  })
    .then((response) => processResponse({ response, onAlert }))
    .catch((error) => {
      onAlert({
        text: `Could not fetch Elearning progress per module data. ${error.name}: ${error.message}`,
        type: ToastTypes.error,
      })
    })
}

export { getElearningRadarReportData, getElearningTimeReportData, getElearningProgressData, getElearningProgressPerModuleData }
